import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { UserInfoContext } from '../Context';
import { usePost } from '../API';

export type Organization = {
  uid: string;
  name: string;
  quota: string;
  metadata: Record<string, any>;
};

type OrganizationListFilters = {
  search?: string;
};

type CreateOrganizationParams = {
  name: string;
  metadata?: string;
};

type UpdateOrganizationParams = {
  orgUid: string;
  name?: string;
  metadata?: string;
};

type CreateOrganizationResult = {
  success: boolean;
  uid?: string;
  created?: boolean;
};

type UpdateOrganizationResult = {
  success: boolean;
};

type DeleteOrganizationResult = {
  success: boolean;
};

export type OrganizationContextType = {
  organizations: Organization[];
  selectedOrganization: Organization | null | undefined;
  setSelectedOrganization: (org: Organization | null | undefined) => void;
  hasOrganizations: boolean;
  canSelectMultiple: boolean;
  loading: boolean;
  error: string | null;
  fetchOrganizations: (filters?: OrganizationListFilters) => Promise<Organization[]>;
  getOrganization: (uid: string) => Promise<any | null>;
  createOrganization: (params: CreateOrganizationParams) => Promise<CreateOrganizationResult>;
  updateOrganization: (params: UpdateOrganizationParams) => Promise<UpdateOrganizationResult>;
  deleteOrganization: (orgUid: string) => Promise<DeleteOrganizationResult>;
};

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
};

type OrganizationProviderProps = {
  children: ReactNode;
};

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userInfo = useContext(UserInfoContext);
  const listOrganizations = usePost('/organization/list');
  const getOrganizationApi = usePost('/organization/get');
  const createOrganizationApi = usePost('/organization/create');
  const updateOrganizationApi = usePost('/organization/update');
  const deleteOrganizationApi = usePost('/organization/delete');

  const fetchOrganizations = useCallback(async (filters?: OrganizationListFilters): Promise<Organization[]> => {
    try {
      const response = await listOrganizations({
        search: filters?.search,
      });

      if (Array.isArray(response)) {
        return response as Organization[];
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }

    return [];
  }, [listOrganizations]);

  const getOrganization = useCallback(async (uid: string): Promise<any | null> => {
    try {
      const response = await getOrganizationApi({ orgUid: uid });
      if (response && typeof response === 'object') {
        if ('result' in response && response.result) {
          return (response as any).result;
        }
        return response;
      }
    } catch (err) {
      console.error('Failed to fetch organization:', err);
    }

    return null;
  }, [getOrganizationApi]);

  const createOrganization = useCallback(async (params: CreateOrganizationParams): Promise<CreateOrganizationResult> => {
    try {
      const response = await createOrganizationApi(params);
      if (response && typeof response === 'object') {
        const { success = false, uid, created } = response as CreateOrganizationResult;
        return { success, uid, created };
      }
    } catch (err) {
      console.error('Failed to create organization:', err);
    }

    return { success: false };
  }, [createOrganizationApi]);

  const updateOrganization = useCallback(async (params: UpdateOrganizationParams): Promise<UpdateOrganizationResult> => {
    try {
      const response = await updateOrganizationApi(params);
      if (response && typeof response === 'object') {
        const { success = false } = response as UpdateOrganizationResult;
        return { success };
      }
    } catch (err) {
      console.error('Failed to update organization:', err);
    }

    return { success: false };
  }, [updateOrganizationApi]);

  const deleteOrganization = useCallback(async (orgUid: string): Promise<DeleteOrganizationResult> => {
    try {
      const response = await deleteOrganizationApi({ orgUid });
      if (response && typeof response === 'object') {
        const { success = false } = response as DeleteOrganizationResult;
        return { success };
      }
    } catch (err) {
      console.error('Failed to delete organization:', err);
    }

    return { success: false };
  }, [deleteOrganizationApi]);

  useEffect(() => {
    let isMounted = true;

    const loadOrganizations = async () => {
      if (!userInfo?.uid) {
        setLoading(false);
        return;
      }

      try {
        setError(null);

        const orgs = await fetchOrganizations();
        if (!isMounted) {
          return;
        }

        setOrganizations(orgs);

        setSelectedOrganization((currentSelection) => {
          if (orgs.length === 1) {
            return orgs[0];
          }

          if (currentSelection) {
            const matchingOrg = orgs.find(org => org.uid === currentSelection.uid);
            return matchingOrg ?? undefined;
          }

          return currentSelection;
        });
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to fetch organizations:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch organizations');
        setOrganizations([]);
        setSelectedOrganization(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      isMounted = false;
    };
  }, [userInfo?.uid, fetchOrganizations]);

  // Determine if user can select from multiple organizations
  const canSelectMultiple = organizations.length > 1;
  const hasOrganizations = organizations.length > 0;

  const contextValue: OrganizationContextType = {
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    hasOrganizations,
    canSelectMultiple,
    loading,
    error,
    fetchOrganizations,
    getOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
  };

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};
